/**
 * @fileoverview 資料ページ等のコンテンツページでコンテンツのイントロダクションとユーザのプロフィールフォームの2カラム構成になっているページレイアウト
 */
<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  nextTick,
  onBeforeUnmount,
} from 'vue'
import throttle from 'lodash/throttle'
import SolutionContentsIntro from '@/components/solutions/SolutionContentsIntro.vue'
import InputLead from '@/components/lead/InputLead.vue'
import UserLeadInfo from '@/components/lead/UserLeadInfo.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import InputCustomFieldItems from '@/components/customFiled/InputCustomFieldItems.vue'
import InputHearingFieldItems from '@/components/hearingField/InputHearingFieldItems.vue'
import InputUserRegistrationForShowContents from '@/components/user/registration/InputUserRegistrationForShowContents.vue'
import PrivacyPolicyAgreement from '@/components/solutions/PrivacyPolicyAgreement.vue'
import leadGen from '@/models/leadGen'
import hearingFieldsUtil from '@/models/hearingFieldsUtil'
import privacyPolicy from '@/pages/_partials/solutionContents/privacyPolicy'
import { useUserStore } from '@/stores/user'
import { useMediaQueryStore } from '@/stores/mediaQuery'
import { isInputed } from '@/utilities/validator'
import scrollUtil from '@/utilities/scrollUtil'

interface InputUserInfo {
  lastName: InputText,
  firstName: InputText,
  lastNameKana: InputText,
  firstNameKana: InputText,
  companyName: InputText,
  industry: InputText,
  scale: InputText,
  listingStatus: InputText,
  profession: InputText,
  professionDomain: InputMultiSelect,
  positionLevel: InputText,
  departmentAndPosition: InputText,
  workMailAddress: InputText,
  phoneNumber: InputText,
  corporateZipcode: InputText,
  corporateAddress1: InputText,
  corporateAddress2: InputText,
  corporateNumber: InputText,
  corporateEmployeeNum: InputNumber,
}

export default defineComponent({
  name: 'LayoutSolutionContentsShowPage',

  components: {
    SolutionContentsIntro,
    InputLead,
    UserLeadInfo,
    ButtonSubmit,
    InputCustomFieldItems,
    InputHearingFieldItems,
    InputUserRegistrationForShowContents,
    PrivacyPolicyAgreement,
  },

  props: {
    contents: {
      type: Object as PropType<SolutionContentItem>,
      required: true,
    },

    form: {
      type: Object as PropType<Form>,
      required: true,
    },

    inputUserInfo: {
      type: Object as PropType<InputUserInfo>,
      required: true,
    },

    inputOptionalNewsletterInfo: {
      type: Object as PropType<OptionalNewsletterSettings>,
      default: () => ({}),
    },

    inputCustomFields: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },

    customFieldConfigItems: {
      type: Array as PropType<CustomFieldConfig[]>,
      default: () => [],
    },

    inputHearingFields: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },

    hearingFieldConfig: {
      type: Object as PropType<HearingFieldConfig>,
      default: () => ({
        nameSuffix: {
          id: '',
          hearingFieldId: '',
          value: '',
          secondaryValue: '',
        },
        sections: [],
      }),
    },

    isFullFilled: {
      type: Boolean,
      default: false,
    },

    corporateBrandColor: {
      type: Object as PropType<{
        primary: string,
        secondary: string,
      }>,
      default: () => ({ primary: '', secondary: '' }),
    },

    submitButtonLabel: {
      type: String,
      required: true,
    },

    hasHeading: {
      type: Boolean,
      default: false,
    },

    /**
     * プレビュー表示かどうか
     * @description 未ログインでもリード情報を入力済みの状態で見せるために、会員登録フローではなくリード情報フォームを描画する
     */
    isPreview: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:lastName',
    'update:firstName',
    'update:lastNameKana',
    'update:firstNameKana',
    'update:companyName',
    'update:industry',
    'update:scale',
    'update:listingStatus',
    'update:profession',
    'update:professionDomain',
    'open:professionDomain',
    'close:professionDomain',
    'update:positionLevel',
    'update:departmentAndPosition',
    'update:workMailAddress',
    'update:phoneNumber',
    'update:corporateZipcode',
    'update:corporateAddress1',
    'update:corporateAddress2',
    'update:customFields',
    'update:hearingFields',
    'select:companyLeadInfo',
    'update:mailStopFlag',
    'update:mailNewsStopFlag',
    'update:mailExpoStopFlag',
    'update:mailAiStopFlag',
    'reset:corporateZipcode',
    'select:hearingMultiSelectPrimary',
    'close:hearingMultiSelectSecondary',
  ],

  setup(props, { emit }) {
    // store
    const userStore = useUserStore()
    const mediaQueryStore = useMediaQueryStore()

    const { privacyPolicyCaption } = privacyPolicy(props.contents.solution!)

    const userLeadDescriptionDOM = ref()
    const submitButtonWrapperDOM = ref()
    const submitButtonRect = ref({
      height: 0,
      position: 0,
    })
    const isSticky = ref(false)
    const initialized = ref(false)

    // プレビューは未ログインでもリード情報フォーム側を描画する
    const isViewerSignedIn = computed(() => userStore.context.isSignedIn || props.isPreview)

    const {
      isEditModeUserLeadInfo,
      isUpdateUserLeadInfo,
      toEditModeUserLeadInfo,
      toEditModeUserLeadInfoByError,
    } = leadGen(props.inputUserInfo)

    // サーバ側の検証（valid?(:contact)）で不備があれば編集モードで開始する（isFullFilled は props 由来で setup 中に確定している）
    if (!props.isFullFilled) toEditModeUserLeadInfo()

    const {
      // computed
      hasBlankItemsInHearingField,
    } = hearingFieldsUtil(props.hearingFieldConfig)

    /**
     * ヒアリング項目の表示をフィルタリング
     * - 編集状態の場合は全て表示
     * - それ以外の場合は、未入力の項目がある場合のみ表示
     * @returns {HearingFieldConfig}
     */
    const filteredHearingFieldConfig = computed<HearingFieldConfig>(() => {
      return {
        nameSuffix: props.hearingFieldConfig!.nameSuffix,
        sections: props.hearingFieldConfig!.sections.map((section) => ({
          id: section.id,
          title: section.title,
          items: isEditModeUserLeadInfo.value
            ? section.items
            : section.items.filter((item) => !isInputed(item.value)),
        })),
      }
    })

    nextTick(() => {
      // スティッキーの初期表示時計算
      setSubmitButtonStickyStatus(scrollUtil.getScrollY())
    })

    /**
     * ヒアリング項目の「その他」に該当する項目が選択された際の処理
     *
     * @param name 対応する追加設問の name 属性値
     * @param value 対応する追加設問の入力要素オブジェクト
     * @description 引数は emit で通知される値を暗黙的に受け取る
     */
    const onSelectInHearingMultiSelectorPrimary = (name: string, value: Input) => {
      emit('select:hearingMultiSelectPrimary', name, value)
    }

    const onValidated = () => {
      if (!initialized.value) initialized.value = true
    }

    let userLeadDescriptionPadding = 0
    const setSubmitButtonStickyStatus = (scrollPosition: number) => {
      // プロフィール全入力済みの会員でない場合（新規登録・もしくは編集モード）、または SP サイズより大きい場合はスティッキーしない
      if (!isViewerSignedIn.value || isEditModeUserLeadInfo.value) {
        isSticky.value = false
        return
      }

      /**
       * SP は iOS で position: sticky の挙動が不十分なので、
       * 自前で sticky 計算する
       */
      if (mediaQueryStore.context.isUnderPhablet) {
        if (userLeadDescriptionDOM.value && submitButtonWrapperDOM.value) {
          submitButtonRect.value.height = submitButtonWrapperDOM.value.getBoundingClientRect().height

          // submit ボタン自体は、position fixed になって通常のレンダリングフローから外れるため、本来の位置は description からの相対位置で求める
          submitButtonRect.value.position = userLeadDescriptionDOM.value.getBoundingClientRect().top + userLeadDescriptionDOM.value.getBoundingClientRect().height + scrollPosition

          if (submitButtonRect.value.position === undefined) return

          // スティッキーしている最中の場合は、 submit ボタンの高さ分を本来の位置に確保しておくために、userLeadDescription の padding-bottom に設定しているので、その分を相殺させる
          userLeadDescriptionPadding = isSticky.value
            ? submitButtonRect.value.height
            : 0
          isSticky.value = (scrollPosition + document.body.clientHeight) < (submitButtonRect.value.position + (submitButtonRect.value.height - userLeadDescriptionPadding))
        }
      } else {
        // PC は CSS で position: sticky が効くのでそちらで対応
        isSticky.value = true
      }
    }

    // スティッキーの初期表示時計算
    const loadListener = () => {
      setSubmitButtonStickyStatus(scrollUtil.getScrollY())
    }

    window.addEventListener('load', loadListener)

    // スクロールイベントによるスティッキー再計算
    const cleanupScroll = scrollUtil.throttle({
      callback: (e = {
        prevScrollPos: 0,
        updateScrollPos: 0,
        isUp: false,
        isDown: false,
        isPageLoad: false,
      }) => {
        if (e.updateScrollPos !== undefined && e.prevScrollPos !== e.updateScrollPos) {
          setSubmitButtonStickyStatus(e.updateScrollPos)
        }
      },
      wait: 30,
    })

    // window のリサイズイベントを監視して、幅に変更があったらスティッキーを再計算する
    const resizeListener = throttle(async () => {
      setSubmitButtonStickyStatus(scrollUtil.getScrollY())
    }, 30)

    window.addEventListener('resize', resizeListener)

    onBeforeUnmount(() => {
      // イベントリスナー解除
      cleanupScroll()
      window.removeEventListener('load', loadListener)
      window.removeEventListener('resize', resizeListener)
    })

    return {
      // store
      userStore,
      mediaQueryStore,

      // data
      userLeadDescriptionDOM,
      submitButtonWrapperDOM,
      submitButtonRect,
      isSticky,
      isEditModeUserLeadInfo,
      initialized,

      // computed
      isViewerSignedIn,
      isUpdateUserLeadInfo,
      privacyPolicyCaption,
      hasBlankItemsInHearingField,
      filteredHearingFieldConfig,

      // methods
      toEditModeUserLeadInfo,
      toEditModeUserLeadInfoByError,
      onValidated,
      onSelectInHearingMultiSelectorPrimary,
    }
  },
})
</script>

<template>
  <section class="section">
    <div class="l-cols__solutionForm">
      <div class="l-cols__solutionForm--col is-summary">
        <SolutionContentsIntro
          :item="contents"
          :brand-color="corporateBrandColor"
          :has-heading="hasHeading">
          <template
            v-if="$slots.thumbImage"
            #thumbImage>
            <slot name="thumbImage" />
          </template>
          <template
            v-if="$slots.body"
            #body>
            <slot name="body" />
          </template>
        </SolutionContentsIntro>
      <!-- /.l-cols__solutionForm col --></div>

      <div
        id="section-user-lead-info"
        class="l-cols__solutionForm--col is-viewerInfo">
        <section class="f-section__viewerInfo">
          <div class="f-section__viewerInfo--body">
            <!-- ログイン時 -->
            <FormDefault
              v-if="isViewerSignedIn"
              :name="form.name"
              :action="form.action"
              :method="form.method"
              :init-validation="true"
              @validated="onValidated"
              @error="toEditModeUserLeadInfoByError">
              <div class="l-unit__userLeadInfo">
                <div
                  ref="userLeadDescriptionDOM"
                  :class="[
                    'l-unit__userLeadInfo--description',
                    { 'is-secureSubmitButtonHeight': isSticky },
                  ]"
                  :style="{
                    paddingBottom: `${isSticky && mediaQueryStore.context.isUnderPhablet ? submitButtonRect.height : 0}px`
                  }">
                  <!-- リード情報入力エリア -->
                  <InputLead
                    v-if="isEditModeUserLeadInfo"
                    :namespace="form.name"
                    :user-lead-info="inputUserInfo"
                    @update:last-name="$emit('update:lastName', $event)"
                    @update:first-name="$emit('update:firstName', $event)"
                    @update:last-name-kana="$emit('update:lastNameKana', $event)"
                    @update:first-name-kana="$emit('update:firstNameKana', $event)"
                    @update:company-name="$emit('update:companyName', $event)"
                    @update:industry="$emit('update:industry', $event)"
                    @update:scale="$emit('update:scale', $event)"
                    @update:listing-status="$emit('update:listingStatus', $event)"
                    @update:profession="$emit('update:profession', $event)"
                    @update:profession-domain="$emit('update:professionDomain', $event)"
                    @open:profession-domain="$emit('open:professionDomain')"
                    @close:profession-domain="$emit('close:professionDomain')"
                    @update:position-level="$emit('update:positionLevel', $event)"
                    @update:department-and-position="$emit('update:departmentAndPosition', $event)"
                    @update:work-mail-address="$emit('update:workMailAddress', $event)"
                    @update:phone-number="$emit('update:phoneNumber', $event)"
                    @select:company-lead-info="$emit('select:companyLeadInfo', $event)"
                    @reset:corporate-zipcode="$emit('reset:corporateZipcode', $event)">
                    <template
                      v-if="$slots.heading"
                      #heading>
                      <slot name="heading" />
                    </template>
                  </InputLead>

                  <UserLeadInfo
                    v-else
                    :user-lead-info="inputUserInfo"
                    :hearing-field-config="hearingFieldConfig"
                    :input-hearing-fields="inputHearingFields"
                    :namespace="form.name"
                    :is-show-hearing-fields="initialized"
                    @reset:corporate-zipcode="$emit('reset:corporateZipcode', $event)">
                    <template #headingLabel>
                      <slot name="headingLabel" />
                    </template>
                  </UserLeadInfo>

                  <!-- アンケートエリア -->
                  <InputCustomFieldItems
                    v-if="inputCustomFields && customFieldConfigItems && initialized"
                    :namespace="form.name"
                    :custom-field-config-items="customFieldConfigItems"
                    :input-custom-fields="inputCustomFields"
                    @update:custom-fields="$emit('update:customFields', $event)" />

                  <!-- ヒアリング項目 -->
                  <InputHearingFieldItems
                    v-if="(hasBlankItemsInHearingField || isEditModeUserLeadInfo) && initialized"
                    :namespace="form.name"
                    :hearing-field-config="filteredHearingFieldConfig"
                    :input-hearing-fields="inputHearingFields"
                    :is-show-section-title="false"
                    @update:hearing-fields="$emit('update:hearingFields', $event)"
                    @select:primary="onSelectInHearingMultiSelectorPrimary"
                    @close:secondary="$emit('close:hearingMultiSelectSecondary', $event)" />
                <!-- /.l-unit__userLeadInfo description --></div>

                <div
                  ref="submitButtonWrapperDOM"
                  :class="[
                    'l-unit__userLeadInfo--action',
                    'is-submit',
                    { 'is-sticky': isSticky },
                  ]"
                  :data-media-size="mediaQueryStore.sizeName">
                  <PrivacyPolicyAgreement :company-name="contents.solution?.company?.name" />

                  <ButtonSubmit
                    name="commit"
                    :caption="privacyPolicyCaption"
                    :is-link="false"
                    is-submit
                    :color="corporateBrandColor.primary">
                    {{ submitButtonLabel }}
                  </ButtonSubmit>
                <!-- /.l-unit__userLeadInfo action --></div>

                <div
                  v-if="!isEditModeUserLeadInfo"
                  class="l-unit__userLeadInfo--action">
                  <p class="p u-text__center u-text__small">
                    <button
                      class="button"
                      @click="toEditModeUserLeadInfo">
                      プロフィールを編集する
                    </button>
                  </p>
                <!-- /.l-unit__userLeadInfo action --></div>
              <!-- /.l-unit__userLeadInfo --></div>

              <input
                class="input"
                type="hidden"
                name="contact[message]"
                value="">
              <input
                v-if="isUpdateUserLeadInfo"
                class="input"
                type="hidden"
                name="contact[with_update]"
                value="1">
            </FormDefault>

            <!-- 未ログイン時（会員登録） -->
            <InputUserRegistrationForShowContents
              v-else
              :namespace="form.name"
              :form="form"
              :user-lead-info="inputUserInfo"
              :input-custom-fields="inputCustomFields"
              :custom-field-config-items="customFieldConfigItems"
              :input-hearing-fields="inputHearingFields"
              :hearing-field-config="hearingFieldConfig"
              :input-optional-newsletter-info="inputOptionalNewsletterInfo"
              :submit-button-caption="privacyPolicyCaption"
              :agreement-company-name="contents.solution?.company?.name"
              @update:last-name="$emit('update:lastName', $event)"
              @update:first-name="$emit('update:firstName', $event)"
              @update:last-name-kana="$emit('update:lastNameKana', $event)"
              @update:first-name-kana="$emit('update:firstNameKana', $event)"
              @update:company-name="$emit('update:companyName', $event)"
              @update:industry="$emit('update:industry', $event)"
              @update:scale="$emit('update:scale', $event)"
              @update:listing-status="$emit('update:listingStatus', $event)"
              @update:profession="$emit('update:profession', $event)"
              @update:profession-domain="$emit('update:professionDomain', $event)"
              @open:profession-domain="$emit('open:professionDomain')"
              @close:profession-domain="$emit('close:professionDomain')"
              @update:position-level="$emit('update:positionLevel', $event)"
              @update:department-and-position="$emit('update:departmentAndPosition', $event)"
              @update:work-mail-address="$emit('update:workMailAddress', $event)"
              @update:phone-number="$emit('update:phoneNumber', $event)"
              @update:corporate-zipcode="$emit('update:corporateZipcode', $event)"
              @update:corporate-address1="$emit('update:corporateAddress1', $event)"
              @update:corporate-address2="$emit('update:corporateAddress2', $event)"
              @select:company-lead-info="$emit('select:companyLeadInfo', $event)"
              @update:custom-fields="$emit('update:customFields', $event)"
              @update:hearing-fields="$emit('update:hearingFields', $event)"
              @update:mail-stop-flag="$emit('update:mailStopFlag', $event)"
              @update:mail-news-stop-flag="$emit('update:mailNewsStopFlag', $event)"
              @update:mail-expo-stop-flag="$emit('update:mailExpoStopFlag', $event)"
              @update:mail-ai-stop-flag="$emit('update:mailAiStopFlag', $event)"
              @select:hearing-multi-select-primary="onSelectInHearingMultiSelectorPrimary"
              @close:hearing-multi-select-secondary="$emit('close:hearingMultiSelectSecondary', $event)">
              <template
                v-if="$slots.step1HeadingDescription"
                #step1HeadingDescription>
                <slot name="step1HeadingDescription" />
              </template>
              <template
                v-if="$slots.step1SubmitLabel"
                #step1SubmitLabel>
                <slot name="step1SubmitLabel" />
              </template>
              <template
                v-if="$slots.step2Heading"
                #step2Heading>
                <slot name="step2Heading" />
              </template>
              <template
                v-if="$slots.step3Heading"
                #step3Heading>
                <slot name="step3Heading" />
              </template>

              <input
                class="input"
                type="hidden"
                name="contact[message]"
                value="">
              <input
                class="input"
                type="hidden"
                name="contact[with_sign_up]"
                value="1">
            </InputUserRegistrationForShowContents>
          <!-- /.f-section__viewerInfo body --></div>
        <!-- /.f-section__viewerInfo --></section>
      <!-- /.l-cols__solutionForm col --></div>
    <!-- /.l-cols__solutionForm --></div>
  </section>
</template>

<style lang="sass" scoped>
// foundation
.section
.f-section__viewerInfo
  $root: &

  & &--body
    padding: 24px
    background-color: rgba($bg-white, 1)

  +breakpoint
    #{$root}--body
      padding: 16px

// layout
.l-cols__solutionForm
  $root: &
  width: 100%
  display: flex

  $margin-size: 40
  $margin-percent: getRelativeValue($margin-size, $global-contents-width-pc)
  $col-width: math.div(($global-contents-width-pc - $margin-size), 2)
  & &--col
    width: getRelativeValue($col-width, $global-contents-width-pc)
    margin-left: $margin-percent

    &:first-child
      margin-left: 0

  +breakpoint
    $margin-size: 20
    display: block

    #{$root}--col
      width: 100%
      margin:
        top: #{$margin-size}px
        left: 0

      &:first-child
        margin-top: 0

.l-unit__userLeadInfo
  & &--action
    &.is-submit
      padding: 16px 0

      &.is-sticky
        &[data-media-size="sp"],
        &[data-media-size="phablet"]
          width: calc(100% - #{$global-prop-sp * 2}px)
          position: fixed
          left: 50%
          bottom: 0
          transform: translateX(-50%)
          padding: 16px
          background: rgba($bg-white, 0.8)
          z-index: 1

        // &[data-media-size="tablet"],
        // &[data-media-size="pc"]
        //   position: sticky
        //   bottom: 0
        //   background-color: rgba($bg-white, 0.5)
        //   backdrop-filter: blur(2px)
        //   -webkit-backdrop-filter: blur(2px)
</style>
